import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkerProfileField } from 'types';
import { IPersonalInformationSchema } from 'modules/views/pages/PersonalInformation/schema';
import { GOV_ID_TYPE_OTHER_OPTION_ID, WORKER_NAME_MAX_LENGTH } from 'constants/common';
import { FieldType, IField } from 'modules/views/pages/PersonalInformation/components/fieldWrapper';
import { shouldShowSkippableIndicator } from 'utils/skippableFieldsUtils';
import { IProfileFieldConstraintsMap } from 'types/profileCustomFields';
import { useAppSelector } from 'modules/state-mgmt/store';
import { GENDER_OPTIONS, YES_NO_OPTIONS } from 'utils/optionsUtil';

const nameInputProps = { inputProps: { maxLength: WORKER_NAME_MAX_LENGTH } };

/**
 * Exported field configuration for Personal Information
 * Used across the app for consistent field mapping
 */
export const PERSONAL_INFO_FIELD_CONFIG: Partial<
  Record<WorkerProfileField, { displayNameKey: string; formField: string; reduxField?: string }>
> = {
  [WorkerProfileField.workerFirstName]: {
    displayNameKey: 'personalInformation.personalInformation.firstName',
    formField: 'firstName',
    reduxField: 'firstName',
  },
  [WorkerProfileField.workerMiddleName]: {
    displayNameKey: 'personalInformation.personalInformation.middleName',
    formField: 'middleName',
    reduxField: 'middleName',
  },
  [WorkerProfileField.workerLastName]: {
    displayNameKey: 'personalInformation.personalInformation.lastName',
    formField: 'lastName',
    reduxField: 'lastName',
  },
  [WorkerProfileField.workerDateOfBirth]: {
    displayNameKey: 'personalInformation.personalInformation.dateOfBirth',
    formField: 'dateOfBirth',
    reduxField: 'dateOfBirth',
  },
  [WorkerProfileField.workerGender]: {
    displayNameKey: 'personalInformation.personalInformation.gender',
    formField: 'gender',
    reduxField: 'gender',
  },
  [WorkerProfileField.workerEthnicity]: {
    displayNameKey: 'personalInformation.personalInformation.ethnicity',
    formField: 'ethnicityId',
    reduxField: 'ethnicity.id',
  },
  [WorkerProfileField.workerPrimaryLanguage]: {
    displayNameKey: 'personalInformation.personalInformation.primaryLanguage',
    formField: 'primaryLanguageId',
    reduxField: 'primaryLanguage.id',
  },
  [WorkerProfileField.workerVeteranStatus]: {
    displayNameKey: 'personalInformation.personalInformation.veteranStatus',
    formField: 'isVeteran',
    reduxField: 'isVeteran',
  },
  [WorkerProfileField.workerAllergies]: {
    displayNameKey: 'personalInformation.personalInformation.allergies',
    formField: 'allergies',
    reduxField: 'allergies',
  },
  [WorkerProfileField.workerLastSSN]: {
    displayNameKey: 'personalInformation.personalInformation.lastSSN',
    formField: 'socialSecurityNumber',
    reduxField: 'socialSecurityNumber',
  },
  [WorkerProfileField.workerGovtIdType]: {
    displayNameKey: 'personalInformation.govtId.idType',
    formField: 'identificationTypeId',
    reduxField: 'identificationType.id',
  },
  [WorkerProfileField.workerGovtIdIssuedBy]: {
    displayNameKey: 'personalInformation.govtId.issuedBy',
    formField: 'identificationGeographicLocationId',
    reduxField: 'identificationGeographicLocation.id',
  },
  [WorkerProfileField.workerGovtIdNumber]: {
    displayNameKey: 'personalInformation.govtId.idNumber',
    formField: 'identificationNumber',
    reduxField: 'identificationNumber',
  },
  [WorkerProfileField.workerGovtIdImage]: {
    displayNameKey: 'personalInformation.govtId.idImage',
    formField: 'governmentDocumentFiles',
    reduxField: 'governmentDocumentFiles',
  },
  [WorkerProfileField.workerProfilePhoto]: {
    displayNameKey: 'personalInformation.profilePhoto',
    formField: 'pictureUrl',
    reduxField: 'pictureUrl',
  },
};

/**
 * Custom hook for managing PersonalInformation field configuration
 * Handles field display names, form field mappings, and dynamic field behavior
 */
export const usePersonalInformationFields = (
  profileFieldConstraintsMap: IProfileFieldConstraintsMap,
  hasReachedConsentForm: boolean,
  values: IPersonalInformationSchema,
) => {
  const { t } = useTranslation();

  const { ethnicities, languages, languageCode, idTypes, states } = useAppSelector(state => ({
    ethnicities: state.cache.ethnicities[state.app?.languageCode || 'en'] || [],
    languages: state.cache.languages[state.app?.languageCode || 'en'] || [],
    languageCode: state.app?.languageCode || 'en',
    idTypes: state.cache.iDTypes[state.app?.languageCode || 'en'] || [],
    states: state.cache.states[state.app?.languageCode || 'en'] || [],
  }));

  // Use the exported field configuration
  const fieldConfig = PERSONAL_INFO_FIELD_CONFIG;

  // Centralized field display names mapping - derived from config
  const getFieldDisplayName = useCallback(
    (field: WorkerProfileField): string => {
      const config = fieldConfig[field];
      return config ? t(config.displayNameKey) : field.toString();
    },
    [fieldConfig, t],
  );

  // Form field mapping - derived from config
  const formFieldMap: Partial<Record<WorkerProfileField, string>> = useMemo(() => {
    return Object.entries(fieldConfig).reduce(
      (acc, [field, config]) => {
        acc[field as WorkerProfileField] = config.formField;
        return acc;
      },
      {} as Partial<Record<WorkerProfileField, string>>,
    );
  }, [fieldConfig]);

  // Field display names for modal - derived from the form field mapping
  const fieldDisplayNames: Partial<Record<WorkerProfileField, string>> = useMemo(() => {
    return Object.keys(formFieldMap).reduce(
      (acc, fieldKey) => {
        const field = fieldKey as WorkerProfileField;
        acc[field] = getFieldDisplayName(field);
        return acc;
      },
      {} as Partial<Record<WorkerProfileField, string>>,
    );
  }, [formFieldMap, getFieldDisplayName]);

  // Helper function to get dynamic form field map that handles special cases
  const getDynamicFormFieldMap = useCallback(
    (formValues: IPersonalInformationSchema): Partial<Record<WorkerProfileField, string>> => {
      const dynamicMap = { ...formFieldMap };

      // Handle the special case for government ID issued by field
      if (formValues.identificationTypeId === GOV_ID_TYPE_OTHER_OPTION_ID) {
        dynamicMap[WorkerProfileField.workerGovtIdIssuedBy] = 'otherIdentificationType';
      } else {
        dynamicMap[WorkerProfileField.workerGovtIdIssuedBy] = 'identificationGeographicLocationId';
      }

      return dynamicMap;
    },
    [formFieldMap],
  );

  // Helper function to create a field object
  const createField = useCallback(
    (workerProfileField: WorkerProfileField, fieldType: FieldType, fieldOptions?: any[], extraProps?: any): IField => {
      const formFieldId = formFieldMap[workerProfileField];
      return {
        name: getFieldDisplayName(workerProfileField),
        isRequired: profileFieldConstraintsMap[workerProfileField]?.isMandatory,
        isVisible: profileFieldConstraintsMap[workerProfileField]?.isVisible,
        id: formFieldId,
        type: fieldType,
        options: fieldOptions,
        extraProps,
        showSkippableIndicator: shouldShowSkippableIndicator(
          workerProfileField,
          profileFieldConstraintsMap,
          hasReachedConsentForm,
          values[formFieldId],
        ),
        isSkippable: profileFieldConstraintsMap[workerProfileField]?.isSkippable,
        skippableTill: profileFieldConstraintsMap[workerProfileField]?.skippableTill,
      };
    },
    [getFieldDisplayName, formFieldMap, profileFieldConstraintsMap, hasReachedConsentForm, values],
  );

  // Generate personal information fields
  const personalInformationFields: IField[] = useMemo(
    () => [
      createField(WorkerProfileField.workerFirstName, FieldType.Text, undefined, nameInputProps),
      createField(WorkerProfileField.workerMiddleName, FieldType.Text, undefined, nameInputProps),
      createField(WorkerProfileField.workerLastName, FieldType.Text, undefined, nameInputProps),
      createField(WorkerProfileField.workerDateOfBirth, FieldType.Date),
      createField(WorkerProfileField.workerEthnicity, FieldType.Picker, ethnicities),
      createField(WorkerProfileField.workerPrimaryLanguage, FieldType.Picker, languages),
      createField(WorkerProfileField.workerVeteranStatus, FieldType.Picker, YES_NO_OPTIONS[languageCode] || []),
      createField(WorkerProfileField.workerAllergies, FieldType.Text),
      createField(WorkerProfileField.workerLastSSN, FieldType.Text, undefined, { inputProps: { maxLength: 4 } }),
    ],
    [createField, ethnicities, languages, languageCode],
  );

  const genderSectionFields: IField[] = useMemo(
    () => [createField(WorkerProfileField.workerGender, FieldType.Radio, GENDER_OPTIONS[languageCode] || [])],
    [createField, languageCode],
  );

  // Generate government ID fields with special logic for issued by field
  const govtIdFields: IField[] = useMemo(() => {
    const fields: IField[] = [createField(WorkerProfileField.workerGovtIdType, FieldType.Picker, idTypes)];

    // Special handling for issued by field
    const issuedByField: IField = {
      name: getFieldDisplayName(WorkerProfileField.workerGovtIdIssuedBy),
      isRequired: profileFieldConstraintsMap[WorkerProfileField.workerGovtIdIssuedBy]?.isMandatory,
      isVisible: profileFieldConstraintsMap[WorkerProfileField.workerGovtIdIssuedBy]?.isVisible,
      id:
        values.identificationTypeId === GOV_ID_TYPE_OTHER_OPTION_ID
          ? 'otherIdentificationType'
          : 'identificationGeographicLocationId',
      type: values.identificationTypeId === GOV_ID_TYPE_OTHER_OPTION_ID ? FieldType.Text : FieldType.Picker,
      options:
        states?.length > 0 &&
        values?.identificationTypeId &&
        values.identificationTypeId !== GOV_ID_TYPE_OTHER_OPTION_ID
          ? states.filter(
              (d: any) =>
                idTypes.find((e: any) => e.id === values.identificationTypeId)?.geographicLocationsIds?.indexOf(d.id) >=
                0,
            )
          : [],
      showSkippableIndicator: shouldShowSkippableIndicator(
        WorkerProfileField.workerGovtIdIssuedBy,
        profileFieldConstraintsMap,
        hasReachedConsentForm,
        values.identificationTypeId === GOV_ID_TYPE_OTHER_OPTION_ID
          ? values.otherIdentificationType
          : values.identificationGeographicLocationId,
      ),
      isSkippable: profileFieldConstraintsMap[WorkerProfileField.workerGovtIdIssuedBy]?.isSkippable,
      skippableTill: profileFieldConstraintsMap[WorkerProfileField.workerGovtIdIssuedBy]?.skippableTill,
    };

    fields.push(issuedByField);
    fields.push(createField(WorkerProfileField.workerGovtIdNumber, FieldType.Text));

    return fields;
  }, [createField, values, getFieldDisplayName, profileFieldConstraintsMap, hasReachedConsentForm, idTypes, states]);

  // Generate government ID image field
  const govtIdImageField = useMemo(
    () => ({
      name: getFieldDisplayName(WorkerProfileField.workerGovtIdImage),
      isRequired: profileFieldConstraintsMap[WorkerProfileField.workerGovtIdImage]?.isMandatory,
      isVisible: profileFieldConstraintsMap[WorkerProfileField.workerGovtIdImage]?.isVisible,
      id: formFieldMap[WorkerProfileField.workerGovtIdImage],
      showSkippableIndicator: shouldShowSkippableIndicator(
        WorkerProfileField.workerGovtIdImage,
        profileFieldConstraintsMap,
        hasReachedConsentForm,
        values.governmentDocumentFiles,
      ),
      isSkippable: profileFieldConstraintsMap[WorkerProfileField.workerGovtIdImage]?.isSkippable,
      skippableTill: profileFieldConstraintsMap[WorkerProfileField.workerGovtIdImage]?.skippableTill,
    }),
    [
      getFieldDisplayName,
      profileFieldConstraintsMap,
      formFieldMap,
      values.governmentDocumentFiles,
      hasReachedConsentForm,
    ],
  );

  const isGovtFieldsSectionVisible = useMemo(
    () => govtIdFields.some(field => field.isVisible) || govtIdImageField.isVisible,
    [govtIdFields, govtIdImageField],
  );

  // Generate profile photo field config
  const profilePhotoField = useMemo(
    () => ({
      name: getFieldDisplayName(WorkerProfileField.workerProfilePhoto),
      isRequired: profileFieldConstraintsMap[WorkerProfileField.workerProfilePhoto]?.isMandatory,
      isVisible: profileFieldConstraintsMap[WorkerProfileField.workerProfilePhoto]?.isVisible,
      id: 'pictureUrl',
      showSkippableIndicator: shouldShowSkippableIndicator(
        WorkerProfileField.workerProfilePhoto,
        profileFieldConstraintsMap,
        hasReachedConsentForm,
        values.pictureUrl,
      ),
      isSkippable: profileFieldConstraintsMap[WorkerProfileField.workerProfilePhoto]?.isSkippable,
      skippableTill: profileFieldConstraintsMap[WorkerProfileField.workerProfilePhoto]?.skippableTill,
    }),
    [getFieldDisplayName, profileFieldConstraintsMap, hasReachedConsentForm, values.pictureUrl],
  );

  return {
    fieldConfig,
    getFieldDisplayName,
    formFieldMap,
    fieldDisplayNames,
    getDynamicFormFieldMap,
    personalInformationFields,
    genderSectionFields,
    govtIdFields,
    govtIdImageField,
    isGovtFieldsSectionVisible,
    profilePhotoField,
  };
};
